/**
 * @fileoverview Simple flowchart visualization utilities.
 * Provides functions for filtering, positioning, and managing flowchart nodes
 * for visual flow representation organized by intents.
 * @module helperMethods/simple_flowchart/index
 */

import _ from "lodash";

/**
 * Represents the complete flowchart data structure with nodes, links, and flows.
 */
interface MappedObject {
  flows: any[];
  links: any[];
  nodes: any[];
  NotFound: any[];
  ActionNotClear: any[];
  intents: any[];
}

/**
 * Represents a flowchart scene with nodes, links, and flows.
 */
interface SceneObject {
  flows: any[];
  links: any[];
  nodes: any[];
}

/**
 * Filters a scene object to include only items related to a specific intent.
 * Returns a cloned scene with filtered links, nodes, and flows.
 *
 * @param {SceneObject} sceneObject - The scene object to filter
 * @param {string} intentId - The intent ID to filter by
 * @returns {SceneObject} Filtered scene object
 *
 * @example
 * const filtered = filterSceneByIntentId(scene, 'greeting_intent');
 * // Returns scene with only greeting_intent related items
 */
function filterSceneByIntentId(sceneObject: SceneObject, intentId: string): SceneObject {
  const filterred = _.cloneDeep(sceneObject);

  filterred.links = _.filter(filterred.links, (item) => _.includes(item.intentId, intentId));
  filterred.nodes = _.filter(filterred.nodes, (item) => _.includes(item.intentId, intentId));
  filterred.flows = _.filter(filterred.flows, (item) => _.includes(item.intentId, intentId));

  return filterred;
}

/**
 * Calculates and assigns X,Y positions for flowchart nodes.
 * Positions nodes in a grid layout based on their connection hierarchy,
 * with the initial node at the origin and subsequent nodes distributed
 * based on their button/quick reply connections.
 *
 * @param {SceneObject} sceneObject - The scene object with nodes to position
 * @returns {SceneObject} Scene object with positioned nodes
 *
 * @example
 * const positioned = nodesPositionSetup(sceneObject);
 * // Nodes now have x, y coordinates for flowchart layout
 */
function nodesPositionSetup(sceneObject: SceneObject): SceneObject {
  const data = _.cloneDeep(sceneObject);

  let indexOfPreviousNodeFromCurrentNode: number;
  let buttonIndex: number;
  let buttonNotFound: boolean = false;
  const [initPosX, initPosY] = [50, 50];
  const [distanceX, distanceY] = [400, 400];

  data.nodes = _.map(data.nodes, (item) => {
    const newItem = {
      ...item,
    };

    const linkToCurrentNode = _.find(data.links, (link) => link.to === item.id);

    if (!linkToCurrentNode) {
      [newItem.x, newItem.y] = [initPosX, initPosY];
    } else {
      indexOfPreviousNodeFromCurrentNode = _.findIndex(
        data.nodes,
        (node) => linkToCurrentNode.from === node.id
      );
      newItem.x = (indexOfPreviousNodeFromCurrentNode + 1) * distanceX + initPosX;

      buttonIndex = _.get(
        data,
        `nodes[${indexOfPreviousNodeFromCurrentNode}].buttons`,
        []
      ).findIndex((button: any) => {
        const buttonIdFromLink =
          linkToCurrentNode.button != null
            ? linkToCurrentNode.button
            : linkToCurrentNode.quickReply;
        const expectedButtonStyleType = linkToCurrentNode.button != null ? "button" : "quickReply";

        const buttonIdIsMatched = button.id === buttonIdFromLink;
        const buttonStyleTypeIsMatched = button.styleType === expectedButtonStyleType;

        const conditionSatisfied = buttonIdIsMatched && buttonStyleTypeIsMatched;

        return conditionSatisfied;
      });

      buttonNotFound = buttonIndex === -1;
      if (buttonNotFound) {
        newItem.y = 0;
      } else {
        newItem.y = buttonIndex * distanceY + initPosY;
      }
    }

    newItem.isLocked = false;

    return newItem;
  });

  return data;
}

/**
 * Sets up flowchart data for visualization by filtering by intent and preparing node positions.
 * Merges nodes and flows, combines buttons and quick replies, and calculates node positions.
 *
 * @param {MappedObject} mappedObject - The complete mapped object with all flowchart data
 * @param {string} intentId - The intent ID to filter and set up
 * @returns {MappedObject} Prepared flowchart data ready for visualization
 *
 * @example
 * const setup = flowchartSetup(allData, 'greeting_intent');
 * // Returns flowchart data filtered and positioned for the greeting intent
 */
function flowchartSetup(mappedObject: MappedObject, intentId: string): MappedObject {
  const data = _.cloneDeep(mappedObject);

  let tempScene: any = null;

  /**
   * description: filter function here is used for filtering nodes, flows, and links that represent with the selected intent ID
   */
  tempScene = filterSceneByIntentId(
    { flows: data.flows, links: data.links, nodes: data.nodes },
    intentId
  );

  data.links = tempScene.links;
  data.nodes = tempScene.nodes;
  data.flows = tempScene.flows;

  /**
   * description: this is to merge nodes and flows, and merge every nodes quickReplies and buttons
   */
  data.nodes = [...data.nodes, ...data.flows];

  data.nodes = _.map(data.nodes, (item) => ({
    ...item,
    buttons: [...item.buttons, ...item.quickReplies],
  }));

  /**
   * description: mapping every nodes positions by default
   */
  tempScene = nodesPositionSetup({
    flows: data.flows,
    links: data.links,
    nodes: data.nodes,
  });

  data.links = tempScene.links;
  data.nodes = tempScene.nodes;
  data.flows = tempScene.flows;

  return data;
}

/**
 * Changes the version of a node in the flowchart and updates all related nodes.
 * Traverses the flowchart from the specified node following goto links,
 * and updates the mapped flow to use the versioned node ID.
 *
 * @param {string} version - The version ID to apply
 * @param {string} id - The base node ID to version
 * @param {string} intentId - The intent ID context
 * @param {any} flowsData - The complete flows data
 * @param {any} currentMappedFlow - The current mapped flow to update
 * @returns {any} Updated mapped flow data with versioned nodes
 *
 * @example
 * const updated = changeVersion('v2', 'node123', 'greeting', flowsData, mappedFlow);
 * // Updates node123 to node123:v2 and all connected nodes
 */
function changeVersion(
  version: string,
  id: string,
  intentId: string,
  flowsData: any,
  currentMappedFlow: any
) {
  let data = _.cloneDeep(flowsData);
  data = flowchartSetup(data, intentId);

  // initialize new nodes array and reduce current nodes and flows data
  const nodes: any = _.reduce(
    data.nodes,
    (prev, current) => ({
      ...prev,
      [current.id]: current,
    }),
    {}
  );
  const flows = _.reduce(
    data.flows,
    (prev, current) => ({
      ...prev,
      [current.id]: current,
    }),
    {}
  );

  // remove nodes and flows from hidden node (unversioned node)
  const nodeId = `${id}:${version}`;

  const iteratingNodes: any = {};
  iteratingNodes[nodeId] = nodes[nodeId];

  let nodesLength = Object.keys(iteratingNodes).length;
  let index = 0;

  while (index < nodesLength) {
    const key = Object.keys(iteratingNodes)[0];
    const node = iteratingNodes[key];

    _.forEach(node.buttons, (button) => {
      if (button.event === "goto") {
        const nextNodeId = button.data;
        const nextNode = nodes[nextNodeId];
        if (nextNode) {
          iteratingNodes[nextNodeId] = nextNode;
        }
      }
    });

    _.forEach(node.quickReplies, (quickReply) => {
      if (quickReply.event === "goto") {
        const nextNodeId = quickReply.data;
        const nextNode = nodes[nextNodeId];
        if (nextNode) {
          iteratingNodes[nextNodeId] = nextNode;
        }
      }
    });

    index++;
    nodesLength = Object.keys(iteratingNodes).length;
  }

  const mappedNodes: any = _.reduce(
    currentMappedFlow,
    (prev, current) => ({
      ...prev,
      [current.id]: current,
    }),
    {}
  );

  const iteratingKeys = Object.keys(iteratingNodes);

  _.forEach(iteratingKeys, (key) => {
    const iteratingNode = iteratingNodes[key];

    mappedNodes[nodeId] = mappedNodes[id];
    delete mappedNodes[id];

    mappedNodes[nodeId] = {
      ...mappedNodes[nodeId],
      id: nodeId,
      label: iteratingNode.label,
      buttons: iteratingNode.buttons,
      quickReplies: iteratingNode.quickReplies,
    };
  });

  data.nodes = Object.values(mappedNodes);

  return data;
}

export { filterSceneByIntentId, nodesPositionSetup, flowchartSetup, changeVersion };
export { MappedObject, SceneObject };
